<template>
  <PageLayout :isFixed="isFixed" class="tickets-page">
    <template slot="header">
      <div
        class="wrap d-md-flex"
        :class="{ 'd-none': state.includes(STATES.CHAT_OPEN) }"
      >
        <div class="d-flex m-3 mx-lg-0 breadcrumb-box">
          <breadcrumb :list="breadcrumbList" />
        </div>

        <div class="d-flex">
          <b-button
            id="btn-new-ticket"
            variant="tertiary"
            size="lg"
            @click="openNewTicket"
          >
            <i class="material-icons btn-new-ticket-icon">message</i>
            <span class="d-none d-lg-inline-block pl-1">
              {{ labels.newTicket }}
            </span>
          </b-button>

          <TicketsFilter
            id="ticketsFilter"
            ref="ticketsFilter"
            class="ml-2 mr-2"
            @applyFilters="applyFilters"
            :filtersByOwner="ownerTickets"
            @filterWithBadgeSelected="showModalEnterprise"
            :title="labels.titleFilter"
          />

          <template v-if="$can('see', 'tickets.export')">
            <ExportData
              id="exportData"
              :title="labels.titleExport"
              :canExport="$can('use', 'tickets.export')"
              @export="handleExportClick"
            />
          </template>
        </div>
      </div>
    </template>
    <template slot="main">
      <div class="card">
        <!-- Card Head -->
        <ToggleButton
          class="mb-0"
          :items="myTickets"
          v-model="ownerTickets"
          v-if="canSeeOtherFeedbacks"
        />
        <header
          class="card-header d-md-flex"
          :class="{ 'd-none': state.includes(STATES.CHAT_OPEN) }"
        >
          <h3 v-if="!isLoading">{{ ticketCount }} {{ labels.feedbacks }}</h3>
          <div class="tags" v-if="!isLoading">
            <span v-if="!queryParams.filters.length">
              {{ labels.filtersToString }}
            </span>
            <small
              v-for="filter in queryParams.filters"
              :key="filter._id"
              class="tag"
              :class="
                `
                ${filter._id} ${
                  filter._id.includes('Date') ? '' : filter.selected._id
                }
                `
              "
            >
              {{
                filter._id.includes('Date')
                  ? getDateFilterName(filter)
                  : filter.selected.name
              }}
            </small>
          </div>
        </header>
        <!-- END - Card Head -->

        <section
          class="tickets"
          v-if="
            state.includes(STATES.TICKETS) ||
              state.includes(STATES.CHAT_OPEN_NEW)
          "
        >
          <!-- Chat box -->
          <Chat
            class="chat"
            ref="chat"
            :class="{ active: state.includes(STATES.CHAT_OPEN) }"
            :chat="chat"
            @closeChat="$router.push({ name: 'feedbacks.tickets' })"
            @sendMessage="onSendMessage"
            @markAsNotRead="markAsNotRead"
            @completeFeedback="completeFeedback"
            @rateMessage="onRateMessage"
          />

          <!-- Empty State -->
          <div
            class="chat empty-state-box d-none d-md-block"
            v-if="!state.includes(STATES.CHAT_OPEN)"
          >
            <img src="tickets/empty-state-feedback.gif" />
            <h3>{{ labels.letsStart }}</h3>
            <h4>{{ labels.letsStartDescr }}</h4>
          </div>
          <!-- END -  Empty State -->

          <!-- END - Chat box -->

          <!-- Ticket List -->
          <TicketList
            class="ticket-list"
            v-if="state.includes(STATES.TICKETS)"
            :items="tickets"
            :activeTicket="activeTicket"
            :hasMoreTickets="hasMoreTickets"
            @itemClick="openChat"
            @loadMore="loadMoreTickets"
          />
          <!-- END - Ticket List -->
        </section>

        <!-- FILTER_EMPTY_RESULT -->
        <!-- END - FILTER_EMPTY_RESULT -->

        <!-- Empty State -->
        <div
          class="empty-state-box"
          v-if="state.includes(STATES.EMPTY_STATE) && !isLoading"
        >
          <video
            class="no-feedback-state"
            muted
            loop
            autoplay
            src="tickets/empty-state-feedback-2.mp4"
          />
          <span>{{ labels.noFeedbacksMessage }}</span>
        </div>

        <div
          v-if="
            isLoading &&
              !state.includes(STATES.TICKETS) &&
              !state.includes(STATES.CHAT_OPEN_NEW)
          "
          class="loading"
        >
          <loading />
          <p>{{ labels.loadingTickets }}</p>
        </div>

        <div
          class="empty-state-box"
          v-if="state.includes(STATES.FILTER_EMPTY_RESULT) && !isLoading"
        >
          <video muted loop autoplay src="home/empty-state-feedback.mp4" />
          <span class="filter-empty-result">
            {{ labels.noFeedbacksSearch }}
          </span>
        </div>
        <!-- END - Empty State -->
      </div>

      <!-- Modal Plans -->
      <ModalPlans
        v-if="!$can('update', 'reports.groups.filters')"
        ref="component-modal-plans"
        :title="labels.enterprisePlan"
      />
      <!-- END - Modal Plans -->
    </template>
  </PageLayout>
</template>

<script>
/* global process */

import { mapGetters } from 'vuex'
import PageLayout from '@/components/Home/PageLayout.vue'
import TicketList from '@/components/Home/Tickets/TicketList.vue'
import TicketsFilter from '@/components/Home/Tickets/TicketsFilter.vue'
import ExportData from '@/components/Reports/ExportData.vue'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import ToggleButton from '@/components/Shared/ToggleButton.vue'
import Chat from '@/components/Home/Tickets/Chat/Chat.vue'
import { isAdminOrManager } from '@/service/user'
import {
  findTickets,
  getTicketThread,
  sendMessage,
  markIsRead,
  saveTicket,
  serializeQuery,
  completeFeedback,
  rateFeedback,
} from '@/service/tickets'
import { pipe, assocPath, assoc } from 'ramda'
import Loading from '@/components/Shared/Loading.vue'
import { hideChat } from '@/helpers/chat'

const STATES = {
  FILTER_EMPTY_RESULT: 'FILTER_EMPTY_RESULT',
  EMPTY_STATE: 'EMPTY_STATE',
  CHAT_OPEN: 'CHAT_OPEN',
  CHAT_OPEN_NEW: 'CHAT_OPEN_NEW',
  TICKETS: 'TICKETS',
  DEFAULT: 'DEFAULT',
}

const defaultStateData = {
  queryParams: {
    page: 1,
    filters: [],
  },
  tickets: [],
  chat: {
    messages: [],
    meta: {
      tags: [],
    },
  },
  ticketCount: 0,
  activeTicket: false,
  filters: [],
  ownerTickets: true,
  STATES,
  hasMoreTickets: false,
  isLoading: false,
}

export default {
  components: {
    PageLayout,
    TicketList,
    Chat,
    Breadcrumb,
    TicketsFilter,
    ModalPlans,
    ExportData,
    ToggleButton,
    Loading,
  },
  data() {
    return Object.assign({}, defaultStateData)
  },

  watch: {
    $route(to) {
      this.init(to.params.ticketId)
    },

    tickets() {
      this.updateChatMeta()
    },

    ownerTickets: {
      immediate: false,
      handler() {
        this.$refs.ticketsFilter.resetFilters()
        this.$router.push({ name: 'feedbacks.tickets' })
      },
    },
  },

  mounted() {
    this.init(this.$route.params.ticketId)
    this.loadTicketList()
  },

  methods: {
    getDateFilterName(filter) {
      const date = this.$moment(filter.selected).format('DD/MM/YYYY')
      return `${filter.label} ${date}`
    },

    init(ticketId = undefined) {
      hideChat()
      this.trackers()
      this.resetState()
      this.updateChatMeta()
      this.setActiveTicketState(ticketId)
    },

    trackers() {
      this.$root.$emit('setPageTitle', { context: this, path: 'feedbacks' })
    },

    resetState() {
      this.queryParams.page = 1
      this.chat = {
        messages: [],
        meta: {
          tags: [],
        },
      }
      this.resetChat()
    },

    resetChat() {
      Object.assign({ reset: () => {} }, this.$refs.chat || {}).reset()
    },

    setActiveTicketState(ticketId) {
      this.activeTicket = ticketId

      if (ticketId === 'new') {
        this.newTicket()
      } else if (ticketId) {
        this.getTicketThread(ticketId)
        markIsRead(ticketId, true).then(() =>
          this.setTicketIsRead(ticketId, true)
        )
      }
    },

    getTicketThread(ticketId) {
      getTicketThread(ticketId).then(
        messages => (this.chat.messages = messages)
      )
    },

    getFiltersToQuery(filters) {
      const dateFilters = filters
        .filter(({ _id }) => _id.includes('Date'))
        .map(f => ({ [f._id]: f.selected }))

      const defaultFilters = filters
        .filter(f => f.selected && !f._id.includes('Date'))
        .map(f => ({ [f._id]: f.selected._id }))

      return [...dateFilters, ...defaultFilters]
    },

    loadTicketList(show = true) {
      const query = Object.assign(
        {
          page: this.queryParams.page,
          perPage: 20,
          owner: this.ownerTickets,
        },
        ...this.getFiltersToQuery(this.queryParams.filters)
      )

      if (show) {
        this.isLoading = true
      }

      findTickets({ query }).then(({ ticketCount, tickets }) => {
        this.isLoading = false
        this.hasMoreTickets = false
        this.$nextTick(() => {
          this.setTicketList(tickets)
          this.setTicketCount(ticketCount)
          this.hasMoreTickets = ticketCount > this.tickets.length
        })
      })
    },

    loadMoreTickets(e) {
      if (e.isVisible) {
        this.queryParams.page++
        this.loadTicketList(false)
      }
    },

    setTicketCount(ticketCount) {
      this.ticketCount = ticketCount
    },

    setTicketList(tickets) {
      if (!tickets.length) this.queryParams.page--

      const merge = (list1, list2) =>
        list2.reduce(
          (acc, cur) => [
            ...acc,
            ...(acc.find(i => i._id === cur._id) ? [] : [cur]),
          ],
          list1
        )

      this.tickets = merge(this.tickets, tickets)
    },

    updateChatMeta() {
      this.chat.meta = this.tickets.find(i => i._id === this.ticketId) || {}
    },

    async openChat(item) {
      this.$router.push({
        name: 'feedbacks.tickets.open',
        params: { ticketId: item._id },
      })
    },

    showModalEnterprise() {
      this.$refs['component-modal-plans'].show()
    },

    onSendMessage({ message, isAnonymous, feeling }) {
      if (this.ticketId === 'new') {
        this.ticketCount++
        return this.saveNewTicket({ message, isAnonymous, feeling })
      }

      sendMessage(this.ticketId, { message, isAnonymous }).then(
        this.updateThread
      )
    },

    onRateMessage({ rating }) {
      rateFeedback(this.ticketId, { rating }).then(() => {
        this.setTicketCompleted(this.ticketId, rating)
        this.$router.push({ name: 'feedbacks.tickets' })
      })
    },

    saveNewTicket({ message, isAnonymous, feeling }) {
      saveTicket({
        message,
        isAnonymous,
        isPositive: feeling === 'positive',
      }).then(item => {
        this.tickets = [...[item], ...this.tickets]
        this.openChat(item)
      })
    },

    markAsNotRead() {
      markIsRead(this.ticketId, false).then(() => {
        this.setTicketIsRead(this.ticketId, false)
        this.$router.push({ name: 'feedbacks.tickets' })
      })
    },

    completeFeedback() {
      completeFeedback(this.ticketId, true).then(() => {
        this.setTicketCompleted(this.ticketId)
        this.$router.push({ name: 'feedbacks.tickets' })
      })
    },

    setTicketCompleted(ticketId, rating = false) {
      this.tickets = this.tickets.map(ticket =>
        Object.assign(ticket, {
          closed: ticket._id === ticketId ? true : ticket.closed,
          rating: ticket._id === ticketId ? rating : ticket.rating,
          lastMessage: ticket._id === ticketId ? {} : ticket.lastMessage,
        })
      )
    },

    setTicketIsRead(ticketId, isRead) {
      this.tickets = this.tickets.map(ticket =>
        Object.assign(ticket, {
          isRead: ticket._id === ticketId ? isRead : ticket.isRead,
        })
      )
    },

    openNewTicket() {
      this.$router.push({
        name: 'feedbacks.tickets.open',
        params: { ticketId: 'new' },
      })
    },

    newTicket() {
      this.resetState()
      this.chat.newTicket = true
    },

    updateThread({ data }) {
      const ticketToUpdate =
        this.tickets.find(i => i._id === this.ticketId) || {}

      const isAnonymous = ticketToUpdate.isAnonymous ? true : data.isAnonymous

      const existTicketToUpdate = ticketToUpdate.totalReplies !== undefined
      const isFromSameAuthor =
        existTicketToUpdate &&
        data.author.firstname === ticketToUpdate.lastMessage.name

      const totalReplies = isFromSameAuthor
        ? ticketToUpdate.totalReplies
        : existTicketToUpdate
        ? ticketToUpdate.totalReplies + 1
        : 0

      const name =
        data.author.firstname === 'Anonymous'
          ? this.labels.anonymous
          : data.author.firstname

      const updateTicket = pipe(
        assocPath(['lastMessage', 'name'], name),
        assocPath(['lastMessage', 'message'], data.message),
        assocPath(['lastMessage', 'createdAt'], data.createdAt),
        assoc('isAnonymous', isAnonymous),
        assoc('totalReplies', totalReplies),
        assoc('closed', false)
      )

      Object.assign(ticketToUpdate, updateTicket(ticketToUpdate))

      this.chat.messages.push(data)
      this.reloadTicketList()
    },

    applyFilters(filters) {
      this.filters = filters
      this.queryParams = { page: 1, filters }
      this.tickets = []
      this.loadTicketList()
    },

    reloadTicketList() {
      this.queryParams.page = 1
      this.loadTicketList()
    },

    exportFile() {
      const env = window.env || process.env
      const query = Object.assign(
        {
          token: localStorage.jwt,
          language: localStorage.languageabrev,
          owner: this.ownerTickets,
        },
        ...this.getFiltersToQuery(this.queryParams.filters)
      )

      const host = env.VUE_APP_EXPORTER_API_HOST
      const url = `${host}/feedbacks/download?${serializeQuery(query)}`

      window.open(url, '_blank')
    },

    handleExportClick() {
      this.$can('use', 'tickets.export')
        ? this.exportFile()
        : this.showModalEnterprise()
    },
  },

  computed: {
    ...mapGetters(['resources']),

    labels() {
      return Object.assign(
        {},
        this.resources.page.home.feedbacks.tickets,
        this.resources.general.buttons
      )
    },

    myTickets() {
      const { myTickets, otherTickets } = this.labels
      return [[myTickets, true], [otherTickets, false]]
    },

    canSeeOtherFeedbacks() {
      return isAdminOrManager() && !this.$can('block', 'tickets.see.others')
    },

    breadcrumbList() {
      return [
        {
          label: this.labels.feedbacks,
        },
      ]
    },

    ticketId() {
      return this.$route.params.ticketId
    },

    state() {
      const states = {
        [STATES.EMPTY_STATE]:
          !this.tickets.length &&
          !this.queryParams.filters.length &&
          this.ticketId !== 'new',
        [STATES.FILTER_EMPTY_RESULT]:
          !this.tickets.length && this.queryParams.filters.length,
        [STATES.TICKETS]: !!this.tickets.length,
        [STATES.CHAT_OPEN]: !!this.ticketId,
        [STATES.CHAT_OPEN_NEW]: this.ticketId === 'new',
      }
      return Object.keys(states).filter(s => states[s]) || [STATES.DEFAULT]
    },

    isFixed() {
      // Se estivermos com empty state, não podemos adicionar a classe isFixed
      const isEmptyState = () =>
        this.state.includes(STATES.EMPTY_STATE) ||
        this.state.includes(STATES.FILTER_EMPTY_RESULT)
      return !isEmptyState()
    },
  },

  beforeDestroy() {
    this.$root.$emit('app:message:hideOnBeforeDestroy')
  },
}
</script>

<style lang="scss">
@import '~@/styles/custom/colors';
.tickets-page {
  // border: 1px solid map-get($theme-colors, 'gray07');
  color: map-get($theme-colors, 'septenary');
  width: 100%;

  header .wrap {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;

    .btn.btn-tertiary {
      padding: 0.5rem 1rem;
      height: 46px !important;

      @media (max-width: 425px) {
        padding: 0.5rem 0rem;
        width: 46px;
      }
    }

    .btn-new-ticket-icon {
      vertical-align: middle;
    }
  }

  .toggle-tickets {
    text-align: center;
    .btn-group {
      .btn {
        background: map-get($theme-colors, 'gray06');
        border: 1px solid map-get($theme-colors, 'gray-dark');
        border-radius: initial !important;
        color: map-get($theme-colors, 'gray-dark');
        font-weight: 500;

        &.active {
          background: white;
          border: 1px solid
            transparentize(map-get($theme-colors, 'purple01'), 0.5);
          color: map-get($theme-colors, 'primary');
          box-shadow: 0 2px 4px
            transparentize(map-get($theme-colors, 'purple02'), 0.75);
        }

        @media (max-width: 425px) {
          font-size: 12px;
        }

        &:first-child {
          border-radius: 35px 0 0 35px !important;
        }

        &:last-child {
          border-radius: 0 35px 35px 0 !important;
        }
      }
    }
  }

  .tags {
    font-size: 14px;
  }

  .card {
    height: inherit;

    .card-header {
      align-items: flex-start;
      flex-direction: column;
      padding: 0 1rem !important;
      height: 90px;

      h3 {
        color: map-get($theme-colors, 'gray04');
        font-size: 1.2rem;
        margin-bottom: 0px;
        padding-top: 1.5rem;
      }

      span {
        color: map-get($theme-colors, 'gray03');
        font-size: 0.9rem;
      }

      .tags {
        height: 50px;
      }
    }
    .empty-state-box {
      align-items: center;
      display: flex;
      flex-direction: column;
      padding: 1rem;
      text-align: center;

      video {
        margin-bottom: 3rem;
        width: 70%;
      }

      .no-feedback-state {
        width: 45%;
      }

      img {
        margin-bottom: 3rem;
        width: 70%;
      }

      span {
        color: map-get($theme-colors, 'gray03');
        font-size: 1.6rem;
      }
    }
  }

  .tickets {
    display: flex;
    flex-direction: row-reverse;
    flex-grow: 1;
    height: calc(100% - 162px);
    width: 100%;

    .ticket-list {
      scroll-behavior: smooth;
      width: 100%;
    }

    .chat {
      border-left: 1px solid map-get($theme-colors, 'gray07');
      flex-basis: 140%;
      flex-grow: 1;
      display: none;

      &.empty-state-box {
        display: block;
      }

      &.active {
        display: block;
      }

      @media (max-width: 425px) {
        &.active {
          & + .ticket-list {
            flex-basis: 0;
          }
        }
      }
    }
  }
}

.loading {
  margin: 0 auto;
  text-align: center;
}
</style>
<style lang="scss" scoped>
@import '~@/styles/custom/colors';
::v-deep .tag {
  color: map-get($theme-colors, 'primary');
  background-color: map-get($theme-colors, 'gray07');
  border-radius: 10px;
  display: inline-block;
  margin: 2px;
  padding: 2px 10px;
  word-break: keep-all;

  &.constructive {
    background-color: map-get($theme-colors, 'orange02');
  }

  &.positive {
    background-color: map-get($theme-colors, 'lima');
  }
}
</style>
